import React, { useContext, useRef, useState, useEffect, useMemo } from 'react';
import AppContext from 'context/Context';
import { useDispatch } from 'react-redux';
import { getCarCalendar } from 'redux/booking/car/carBookingSlice';
import { TwDropdown } from 'components/common';
import BookingCalendarFrame from '../calendar/BookingCalendarFrame';
import { toCalendarEvent, legendOf } from '../calendar/calendarEvents';
import CalendarEventModal from './CalendarEventModal';

const viewName = ['Month View', 'Week View', 'Day View', 'List View', 'Year View'];
const bookingName = ['Booking view', 'Driver View', 'Car View'];
const toOpts = (arr) => arr.map((v) => ({ value: v, label: v }));

const VIEW_MAP = {
  'Month View': 'dayGridMonth',
  'Week View': 'timeGridWeek',
  'Day View': 'timeGridDay',
  'List View': 'listWeek',
  'Year View': 'listYear',
};

const CarCalendar = () => {
  const { config: { isRTL } } = useContext(AppContext);
  const calendarRef = useRef();
  const [calendarApi, setCalendarApi] = useState({});
  const [currentFilter, setCurrentFilter] = useState(viewName[0]);
  const [currentFilterBooking, setCurrentFilterBooking] = useState(bookingName[0]);
  const [title, setTitle] = useState('');
  const [isOpenEventModal, setIsOpenEventModal] = useState(false);
  const [modalEventContent, setModalEventContent] = useState({});
  const [initialEvents, setInitialEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  // Colour = booking status (approved / pending / rejected…), shown as the
  // event's accent and explained by the legend.
  const events = useMemo(() => (initialEvents || []).map(toCalendarEvent), [initialEvents]);
  const legend = useMemo(() => legendOf(events, (e) => e.status_name), [events]);

  const handleEventClick = (info) => {
    if (info.event.url) {
      window.open(info.event.url);
      info.jsEvent.preventDefault();
    } else {
      setModalEventContent(info);
      setIsOpenEventModal(true);
    }
  };

  const syncTitle = () => setTitle(calendarApi.getCurrentData().viewTitle);
  const nav = (fn) => { calendarApi[fn](); syncTitle(); };

  const handleFilter = (filter) => {
    setCurrentFilter(filter);
    calendarApi.changeView(VIEW_MAP[filter] || 'listYear');
    syncTitle();
  };

  useEffect(() => {
    setCalendarApi(calendarRef.current.getApi());
    if (calendarApi && calendarApi.view) {
      setTimeout(() => {
        const view = calendarApi.view;
        const params = {
          calendar_type: currentFilterBooking.replace(/ /g, '-').toLocaleLowerCase(),
          from_date: view.activeStart,
          end_date: view.activeEnd,
        };
        setLoading(true);
        dispatch(getCarCalendar(params))
          .then((r) => setInitialEvents(Array.isArray(r.payload) ? r.payload : []))
          .finally(() => setLoading(false));
      }, 100);
    }
  }, [calendarApi, title, currentFilterBooking, dispatch]);

  return (
    <div className="tw-p-4">
      <BookingCalendarFrame
        ref={calendarRef}
        title={title || calendarApi.currentDataManager?.data?.viewTitle}
        loading={loading}
        isRTL={isRTL}
        events={events}
        legend={legend}
        onPrev={() => nav('prev')}
        onNext={() => nav('next')}
        onToday={() => nav('today')}
        onEventClick={handleEventClick}
        filters={(
          <>
            <TwDropdown value={currentFilterBooking} onChange={setCurrentFilterBooking} options={toOpts(bookingName)} menuWidth={180} />
            <TwDropdown value={currentFilter} onChange={handleFilter} options={toOpts(viewName)} menuWidth={160} />
          </>
        )}
      />

      <CalendarEventModal
        isOpenEventModal={isOpenEventModal}
        setIsOpenEventModal={setIsOpenEventModal}
        modalEventContent={modalEventContent}
      />
    </div>
  );
};

export default CarCalendar;
